function positiveNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function activeOrthoNextVisit(value) {
  if (!value || typeof value !== "object") return null;
  if (value.status !== "needs_booking") return null;
  if (!value.case_id || !value.current_ortho_visit_id) return null;
  return value;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function checkoutNeedIdsForCommit(values) {
  const seen = new Set();
  const out = [];
  for (const value of Array.isArray(values) ? values : []) {
    const id = typeof value === "string" ? value.trim() : "";
    if (!UUID_RE.test(id) || seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
}

export function checkoutNeedIdsForBundle(bundle) {
  return checkoutNeedIdsForCommit((bundle?.needs || []).map((need) => need?.id));
}

export function orthoBundleDecisionKey(value) {
  const request = activeOrthoNextVisit(value);
  if (!request) return null;
  return [
    request.case_id,
    request.current_ortho_visit_id,
    request.next_visit_type || "adjustment",
    request.interval_weeks || "",
  ].join(":");
}

export function resolvedOrthoNextVisitDecision(requestValue, decision) {
  const key = orthoBundleDecisionKey(requestValue);
  if (!key || decision?.key !== key) return null;
  if (!["booked", "unsure"].includes(decision?.kind)) return null;
  return decision;
}

export function orthoBundleBookingNote(request) {
  const interval = positiveNumber(request?.interval_weeks);
  const visitNumber = positiveNumber(request?.next_visit_number);
  const isDebonding = request?.next_visit_type === "debonding";
  const parts = [isDebonding ? "Ortho debonding" : "Next Ortho adjustment"];
  if (visitNumber) parts.push(`Visit ${visitNumber}`);
  if (interval) parts.push(`${interval}w interval`);
  return parts.join(" · ");
}

export function buildBundleableOrthoNeed(requestValue, decision) {
  const request = activeOrthoNextVisit(requestValue);
  const key = orthoBundleDecisionKey(request);
  if (!request || !key) return null;
  if (resolvedOrthoNextVisitDecision(request, decision)) return null;

  const intervalDays = positiveNumber(request.interval_days);
  if (!intervalDays || !request.target_date) return null;

  return {
    key: `ortho:${key}`,
    procedure_id: request.procedure_id || null,
    procedure_name: request.procedure_name || "Ortho Adjustment",
    subtotalCents: Number(request.expected_charge_centavos || 0),
    estimatedMinutes: positiveNumber(request.duration_minutes) || 15,
    needs: [],
    doctorRequestedDays: intervalDays,
    followUpKind: "future_need",
    appointmentInputExtra: {
      procedure_id: request.procedure_id || undefined,
      ortho_case_id: request.case_id,
      ortho_visit_type: request.next_visit_type || "adjustment",
    },
    initialNotes: orthoBundleBookingNote(request),
  };
}

export function withOrthoBundle(bundle, orthoNeed) {
  if (!bundle || !orthoNeed) return bundle;
  const treatmentNeedCount = Array.isArray(bundle.needs) ? bundle.needs.length : 0;
  const totalNeedCount = treatmentNeedCount + 1;
  const syntheticNeed = {
    id: orthoNeed.key,
    procedure_id: orthoNeed.procedure_id,
    procedure_name: orthoNeed.procedure_name,
    default_fee_centavos: orthoNeed.subtotalCents,
    follow_up_after_days: orthoNeed.doctorRequestedDays,
    is_ortho_next_visit: true,
  };
  return {
    ...bundle,
    procedure_name: `${totalNeedCount} future needs`,
    needs: [...(bundle.needs || []), syntheticNeed],
    subtotalCents: Number(bundle.subtotalCents || 0) + Number(orthoNeed.subtotalCents || 0),
    estimatedMinutes: Number(bundle.estimatedMinutes || 0) + Number(orthoNeed.estimatedMinutes || 0),
    orthoNeed,
    appointmentInputExtra: orthoNeed.appointmentInputExtra,
    initialNotes: orthoNeed.initialNotes,
    doctorRequestedDays: Math.min(
      ...[bundle.doctorRequestedDays, orthoNeed.doctorRequestedDays]
        .map((value) => positiveNumber(value))
        .filter(Boolean),
    ),
  };
}
